import React from 'react';
import { RoughNotationProps } from 'react-rough-notation';
import type { QuickRecallMark, ShareChannel, TrackKey } from '@libertify/ui';
import { components } from '@libertify/api-types/media';
import type { IHighlight } from 'react-pdf-highlighter';
import Player from 'video.js/dist/types/player';

export interface Message {
  id: string;
  message: string | null;
  sender: 'assistant' | 'user';
  isLoading?: boolean;
}

/** Controls the size of the mobile PIP video. Only affects mobile view with document animation. */
export type PipVideoSize = 'small' | 'large';

export type PublicationIdentifiers =
  | {
      projectId: string;
      publicationId: string;
      organizationSlug?: never;
      instanceSlug?: never;
      organizationId?: string;
    }
  | {
      projectId?: never;
      publicationId?: never;
      organizationSlug: string;
      instanceSlug: string;
      organizationId?: string;
    }
  // It acts as a catch-all/fallback for when we don't have complete data yet. The discriminated union still provides value by ensuring you can't mix UUID-based and slug-based identifiers when they ARE defined.
  | {
      projectId?: string;
      publicationId?: string;
      organizationSlug?: string;
      instanceSlug?: string;
      organizationId?: string;
    };

export type CustomTheme = {
  colors: {
    primary: string;
    ctaTextColor: string;
    secondary: string;
    highlight: string;
  };
  fontFamily: string;
};

export type ChatMessage = {
  query_id: string;
  chunk: string;
  suggested_questions: string[];
  action: string;
  queryId: string;
  query: string;
  message: string;
  is_final?: boolean;
  suggested_queries?: string[];
  source_documents?: MessageOrigin[];
  user_query: string;
  messages?: {
    content: string;
    role: 'assistant' | 'user';
    timestamp: string;
  }[];
  metadata?: {
    sources: MessageOrigin[] | undefined;
    suggested_questions?: string[];
  };
};

export type MessageOrigin = {
  document_id: string;
  occurrences: MessageOccurrence[];
  text: string;
  zone?: {
    quote: string;
    position: {
      rects: any[];
      page_number: number;
      bounding_rect: {
        x1: number;
        y1: number;
        x2: number;
        y2: number;
        width: number;
        height: number;
      };
    };
  };
};

export type MessageOccurrence = {
  chunk: string;
  coordinates: number[];
  page_number: number;
};

export type IRoughNotation = Omit<RoughNotationProps, 'children'>;

export type Cuepoint = {
  documentId: string;
  highlightId: string;
  start: number;
  top?: number;
  title?: string;
  roughNotation?: IRoughNotation;
  zoom?: {
    scale: number;
  };
};

export type Section = {
  title: string;
  startTime: number;
};

export type MediaProjectInstanceResponse =
  components['schemas']['MediaProjectInstanceResponse'] & {
    // TODO(MED-6701): remove once BE ships these on the /live response
    tracking_token?: string | null;
    // Live on dev, not yet on staging — so the staging-generated media.d.ts
    // doesn't carry it. Absent means "field not deployed", not "no add-ons".
    enabled_add_ons?: AddOnType[];
  };

export type MediaProjectResponse = components['schemas']['MediaProjectResponse'];
export type ProjectMediaResponse = components['schemas']['ProjectMediaResponse'];

export type ProjectDocument = components['schemas']['Document'];

export type AddOnType = components['schemas']['AddOnType'];
export type AddOnState = components['schemas']['ProjectAddOnStateResponse'];

export type MediaAsset = components['schemas']['MediaAsset'];
/** Body of `PATCH /media/assets/{assetId}`, the add-on content write. */
export type AssetPatchBody =
  components['schemas']['Body_replace_asset_media_assets__asset_id__patch'];

// Add-on asset contents.
export type ExecutiveSummary = components['schemas']['ExecutiveSummary'];
export type Quiz = components['schemas']['Quiz'];
export type Flashcards = components['schemas']['Flashcards'];
export type Standouts = components['schemas']['Standouts'];

export type SourceZone = components['schemas']['SourceZone'];
export type BoundingRect = components['schemas']['BoundingRect'];

/** The part of a pdf.js `PageViewport` the zone conversion needs. */
export interface PdfPointViewport {
  width: number;
  height: number;
  /** The page's `[xMin, yMin, xMax, yMax]` in PDF points. */
  viewBox: [number, number, number, number];
  convertToPdfPoint: (x: number, y: number) => [number, number];
}

/** Receives a text selection as a zone; `clearSelection` drops the browser's
 *  selection highlight once the caller is done with it. */
export type TextSelectionHandler = (
  zone: SourceZone,
  clearSelection: () => void,
) => void;

export type CallToActions = components['schemas']['CallToActions'];
export type CallToAction = components['schemas']['CallToAction'];

/** `GET` / `PATCH /media/tutorial` response: the student's tutorial flag. */
export type TutorialStatus = components['schemas']['TutorialInteractionResponse'];
/** Body of `PATCH /media/tutorial`. */
export type UpdateTutorialRequest =
  components['schemas']['UpdateTutorialInteractionRequest'];

export type Layout = 'video' | 'chat';

export type ApiError = { detail: { message: string } | string };

/**
 * Shared props for error page components
 */
export type ErrorPageProps = {
  isDocustream: boolean;
};

// Types for Analytics

import type { BaseSuperProperties } from '@libertify.frontend/analytics';

/**
 * Super properties included in every event for the media player.
 * @superProperties
 */
export type AppSuperProperties = BaseSuperProperties<'web_experience'> & {
  /** Current project ID */
  project_id: string;
  /** Current publication ID */
  publication_id: string;
  /**
   * The publication the viewer was served (`project_publication_id` from /live),
   * which the backend splits the analytics by. Absent on previews, which send no events.
   */
  project_publication_id?: string;
  /** Organization owning the content */
  organization_id: string;
  /** Workspace owning the project. Absent when the publication has none. */
  workspace_id?: string;
  /**
   * The authenticated visitor's tracking token. Absent for anonymous access —
   * the backend only issues one for authenticated visitors.
   */
  tracking_id?: string;
  /** Whether the player is rendered in embed mode */
  embed: boolean;
};

export type CommonProperties = {
  project_id: string;
  publication_id: string;
  project_type: ProjectType;
};
export type ProjectType = 'newsletter' | 'hey_genius' | 'new_video';

export enum MediaAnalyticsEvents {
  PublicationView = 'publication_view',
  ChatOpenEvent = 'chat_open',
  ChatCloseEvent = 'chat_close',
  DocumentScrollStarted = 'document_scroll_start',
  DocumentScrollStopped = 'document_scroll',
  SuggestedQuerySelected = 'suggested_query_select',
  ChatFollowUpClick = 'chat_follow_up_click',
  ChatSourceClick = 'chat_source_click',
  ChatInputFocus = 'chat_input_focus',
  ChatMessageSend = 'chat_message_send',
  DocumentZoom = 'document_zoom',
  CtaClick = 'cta_click',
  ChapterClick = 'media_player_chapter_click',
  ChaptersOpen = 'media_player_chapters_open',
  ChaptersClose = 'media_player_chapters_close',
  BrandingLinkClick = 'branding_link_click',
  AutoPromoLinkClick = 'autopromo_link_click',
  AutoPromoBannerDismiss = 'autopromo_banner_dismiss',
  /** A Knowledge Hub add-on tab was opened (e.g. the viewer clicks "Quiz"). */
  AddonTabOpen = 'addon_tab_open',
  /**
   * A Knowledge Hub add-on tab lost focus because another tab was opened.
   * Tabs have no close affordance, so this is the only close there is —
   * always paired with a prior `addon_tab_open` of the same tab.
   */
  AddonTabClose = 'addon_tab_close',
  /** The viewer submitted the first answer of a new or resumed attempt in this visit. */
  QuizStart = 'quiz_start',
  /** The viewer answered a quiz question. */
  QuizAnswer = 'quiz_answer',
  /** The viewer finished a quiz attempt. */
  QuizAttempt = 'quiz_attempt',
  /** A Quick recall card became the visible card (initial card + changes). */
  QuickRecallItemReveal = 'quickrecall_item_reveal',
  /** The viewer flipped a Quick recall card to its answer. */
  QuickRecallItemFlip = 'quickrecall_item_flip',
  /** The viewer stepped through the deck with the Prev/Next buttons. */
  QuickRecallItemNavigate = 'quickrecall_item_navigate',
  /** The viewer self-rated a card ("I knew this" / "New to me"). */
  QuickRecallItemRate = 'quickrecall_item_rate',
  /** The viewer expanded a key point in the Key points add-on. */
  KeyPointsItemExpand = 'key_points_item_expand',
  /** The viewer clicked a standout row (jumps to its mark in the document). */
  StandoutsItemClick = 'standouts_item_click',
  /** Audio add-on: play pressed. */
  PodcastPlay = 'podcast_play',
  /** Audio add-on: pause pressed (track end is `podcast_complete`). */
  PodcastPause = 'podcast_pause',
  /** Audio add-on: the +15s skip button. */
  PodcastForward = 'podcast_ff',
  /** Audio add-on: the −15s skip button. */
  PodcastRewind = 'podcast_rw',
  /** Audio add-on: the playback speed was changed. */
  PodcastSpeed = 'podcast_speed',
  /** Audio add-on: one progress-bar seek gesture (drag or click). */
  PodcastSeek = 'podcast_seek',
  /** Audio add-on: the track played to its end. */
  PodcastComplete = 'podcast_complete',
  /** Audio add-on: every 5 s while the audio plays (auto-play included). */
  PodcastHeartbeat = 'podcast_heartbeat',
  /** The viewer opened the Share link modal. */
  ShareLinkOpen = 'share_link_open',
  /** The viewer copied the share link, or opened a social/email share deep link to it. */
  ShareIntent = 'share_intent',
  /** LTI: the tutorial modal opened (header button or first-visit auto-open). */
  TutorialOpen = 'tutorial_open',
  /** LTI: the tutorial modal closed (close button, Esc or a click outside). */
  TutorialClose = 'tutorial_close',
  /** The document was shown in the viewer (once per page load). */
  DocumentImpression = 'document_impression',
}

/** Knowledge Hub add-on tabs. Matches the tab keys the apps compose. */
export const ADDON_KEYS = [
  'video',
  'audioDoc',
  'podcast',
  'summary',
  'quiz',
  'bookmarks',
  'flashcards',
  'standouts',
] as const;
export type AddonKey = (typeof ADDON_KEYS)[number];

/**
 * Runtime guard for the (string-typed) key the Tabs `onChange` hands back, so an
 * unknown tab can never be sent as an analytics `addon` value.
 */
export const isAddonKey = (key: string): key is AddonKey =>
  (ADDON_KEYS as readonly string[]).includes(key);

/**
 * A single answered quiz question. The `selected_option_indices` /
 * `is_correct` arrays support both single- and multi-select questions.
 */
export interface QuizAnswerRecord {
  /** Position within this attempt's drawn questions. */
  question_index: number;
  /** Position in the full published pool, before the random draw. */
  pool_question_index: number;
  selected_option_indices: number[];
  is_correct: boolean[];
}
export type MediaEventsWithProperties = {
  [MediaAnalyticsEvents.PublicationView]: {
    publication_title: string;
  };
  [MediaAnalyticsEvents.DocumentScrollStarted]: {
    scroll_y: number;
    current_page: number;
    total_pages?: number;
    scroll_source: 'user' | 'highlight';
  };
  [MediaAnalyticsEvents.DocumentScrollStopped]: {
    scroll_y: number;
    current_page: number;
    total_pages?: number;
    scroll_source: 'user' | 'highlight';
  };
  [MediaAnalyticsEvents.ChatOpenEvent]: { chat_session_id?: string };
  [MediaAnalyticsEvents.ChatCloseEvent]: { chat_session_id?: string };
  [MediaAnalyticsEvents.SuggestedQuerySelected]: {
    query: string;
    chat_session_id?: string;
  };
  [MediaAnalyticsEvents.ChatSourceClick]: {
    source_index: number;
    chat_session_id?: string;
  };
  [MediaAnalyticsEvents.ChatInputFocus]: {
    input_position: InputPosition;
    chat_session_id?: string;
  };
  [MediaAnalyticsEvents.ChatMessageSend]: {
    input_position: InputPosition;
    query_source: ChatQuerySource;
    chat_session_id?: string;
  };
  [MediaAnalyticsEvents.DocumentZoom]: {
    zoom_level: number;
    zoom_action: ZoomAction;
  };
  [MediaAnalyticsEvents.CtaClick]: CtaClick;
  [MediaAnalyticsEvents.ChatFollowUpClick]: {
    // add a specific property for new events from this fix
    // to be able to identify new events and filter then in backend
    // if necessary
    query_source: ChatFollowupQuerySource;
    chat_session_id?: string;
  };
  [MediaAnalyticsEvents.ChapterClick]: {
    chapter_index: number;
    chapter_start_time: number;
  };
  [MediaAnalyticsEvents.ChaptersOpen]: {
    chapters_count: number;
  };
  [MediaAnalyticsEvents.ChaptersClose]: {
    chapters_count: number;
  };
  [MediaAnalyticsEvents.BrandingLinkClick]: {
    link_text: string;
    link_location: string;
  };
  [MediaAnalyticsEvents.AutoPromoLinkClick]: {
    link_url: string;
    link_location: string;
    link_text: string;
  };
  [MediaAnalyticsEvents.AutoPromoBannerDismiss]: {
    link_location: string;
  };
  [MediaAnalyticsEvents.AddonTabOpen]: {
    addon: AddonKey;
  };
  [MediaAnalyticsEvents.AddonTabClose]: {
    /** The tab that lost focus (not the one that took it). */
    addon: AddonKey;
  };
  [MediaAnalyticsEvents.QuizStart]: {
    /** True when continuing an attempt restored from an earlier visit. */
    resume: boolean;
  };
  [MediaAnalyticsEvents.QuizAnswer]: QuizAnswerRecord;
  [MediaAnalyticsEvents.QuizAttempt]: {
    answers: QuizAnswerRecord[];
    /** Hardcoded to 1 until a read-attempts endpoint exists (see hook). */
    attempt_id: number;
    /** Percentage correct, 0–100. */
    score: number;
    total_correct: number;
  };
  [MediaAnalyticsEvents.QuickRecallItemReveal]: {
    /** Zero-based index of the card now visible. */
    item_index: number;
    /** Deck size, so reveal depth reads as a fraction without a join. */
    total_items: number;
  };
  [MediaAnalyticsEvents.QuickRecallItemFlip]: {
    /** Zero-based index of the flipped card. */
    item_index: number;
    /** Flips of this card within the session (revisits keep counting). */
    flip_count: number;
  };
  [MediaAnalyticsEvents.QuickRecallItemNavigate]: {
    /** Zero-based index of the destination card. */
    item_index: number;
    direction: 'prev' | 'next';
  };
  [MediaAnalyticsEvents.QuickRecallItemRate]: {
    /** Zero-based index of the rated card. */
    item_index: number;
    /** The self-rating: `known` = "I knew this", `review` = "New to me". */
    rating: QuickRecallMark;
  };
  [MediaAnalyticsEvents.KeyPointsItemExpand]: {
    /** Zero-based index of the expanded key point. */
    item_index: number;
    /** Number of key points in the accordion. */
    total_items: number;
  };
  [MediaAnalyticsEvents.StandoutsItemClick]: {
    /** Zero-based index of the clicked standout row. */
    item_index: number;
    /** Number of standouts in the list. */
    total_items: number;
  };
  [MediaAnalyticsEvents.PodcastPlay]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.PodcastPause]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.PodcastForward]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.PodcastRewind]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.PodcastSpeed]: PodcastPlaybackProperties & {
    /** The selected playback rate (0.75–2). */
    speed: number;
  };
  [MediaAnalyticsEvents.PodcastSeek]: PodcastPlaybackProperties & {
    /** Playhead before the seek gesture, in seconds. */
    seek_from: number;
    /** Playhead after the seek gesture, in seconds. */
    seek_to: number;
  };
  [MediaAnalyticsEvents.PodcastComplete]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.PodcastHeartbeat]: PodcastPlaybackProperties;
  [MediaAnalyticsEvents.ShareIntent]: {
    /** Which action the viewer took: the Copy button, or a social/email channel. */
    channel: 'copy' | ShareChannel;
  };
  [MediaAnalyticsEvents.TutorialOpen]: {
    /** True for the first-visit auto-open, false for the header button. */
    auto_opened: boolean;
  };
  [MediaAnalyticsEvents.TutorialClose]: {
    /** Same value as the `tutorial_open` this close belongs to. */
    auto_opened: boolean;
  };
};

/**
 * Common payload of every `podcast_*` event. The Audio add-on plays two
 * tracks through the same controls — `track` says which one the event is
 * about, so AudioDoc listening never pollutes podcast numbers.
 */
export type PodcastPlaybackProperties = {
  track: TrackKey;
  /** Playhead in whole seconds when the control was used (pre-action). */
  position_seconds: number;
  /** Track length in whole seconds (0 until the audio metadata loads). */
  duration_seconds: number;
  /**
   * Whole seconds of the audio played since the previous `podcast_*` event of
   * this player (0 on the first), seeks excluded. Audio time, not clock time:
   * 10 s at 2x is 20. A delta; analytics sums them.
   */
  listened_seconds: number;
  /** Playback speed when the event fires (pre-action on `podcast_speed`). */
  playback_rate: number;
};

export type InputPosition = 'floating' | 'chat';
export type ChatQuerySource = 'user' | 'suggested';
export type ChatFollowupQuerySource = 'suggested';
type ZoomAction = 'zoom_in' | 'zoom_out' | 'zoom_reset';
type CtaClick = { cta_type: string; cta_label: string };

export interface VideoJsOptions {
  autoplay: boolean;
  muted: boolean;
  controls: boolean;
  fluid: boolean;
  disablePictureInPicture: boolean;
  width: number;
  sources: VideoSource[];
  experimentalSvgIcons: boolean;
  controlBar: VideoControlBarOptions;
  poster?: string;
}

export interface VideoSource {
  src: string;
  type: string;
}

export interface VideoControlBarOptions {
  fullscreenToggle: boolean;
  pictureInPictureToggle: boolean;
  playbackRateMenuButton: boolean;
  remainingTimeDisplay: boolean;
  volumePanel: boolean;
  skipButtons: SkipButtonsConfig;
}

/**
 * Speed options offered by the playback-rate menu. Also restored when a
 * SCORM no-seek lesson completes and the rate lock lifts.
 */
export const DEFAULT_PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2];

export interface SkipButtonsConfig {
  // Both are optional: video.js renders each skip button only when its value
  // is set. (SCORM no-seek mode hides the forward button via CSS — see
  // .vjs-no-seek in styles/app.css — so it can reappear once the lesson
  // completes and seeking unlocks.)
  forward?: number;
  backward?: number;
}

export interface LeftPanelProps {
  showSideCtas: boolean;
  videoOnly: boolean;
  publication: MediaProjectInstanceResponse;
  mainDocument: ProjectDocument | undefined;
  videoJsOptions: VideoJsOptions;
  playerRef: React.RefObject<Player | null>;
  cuepoints: Cuepoint[];
  highlights: IHighlight[];
  sections: Section[];
  callToActions: CallToActions | undefined;
  organizationId: string | undefined;
  onPlayerReady: (player: Player) => void;
  showLogo: boolean;
  locale: string;
  isVideoLoaded: boolean;
  isCreatorMode?: boolean;
  primaryColor?: string;
  /** Optional content to render instead of the video player */
  videoOverride?: React.ReactNode;
}

export interface RightPanelProps {
  showTitle: boolean;
  showLogo: boolean;
  publication: MediaProjectInstanceResponse;
  hasChat: (publication?: MediaProjectInstanceResponse) => boolean;
  mainDocument: ProjectDocument | undefined;
  hasDocumentAnimation: (publication?: MediaProjectInstanceResponse) => boolean;
  highlights: IHighlight[];
  cuepoints: Cuepoint[];
  pdfViewerRef: React.MutableRefObject<never>;
  organizationId: string | undefined;
  /** Creator mode: content rendered inside the document card (e.g. chat input) */
  creatorDocumentFooter?: React.ReactNode;
  /** Whether creator mode is active */
  isCreatorMode?: boolean;
  /** Chat state */
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  chatHeight: number;
  onDragStart: (e: React.MouseEvent) => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
  /** Chat data props */
  firstQuery: string | undefined;
  nextQuery: string | undefined;
  openChat: boolean;
  suggestions: string[] | undefined;
  setSuggestions: (suggestions: string[] | undefined) => void;
  documentIds: string[];
  handleDisplaySource: (source: MessageOrigin) => void;
  cleanHighlightAndCuepoints: () => void;
}

export enum ERROR_DETAIL_MESSAGES {
  NOT_FOUND = 'Not found',
}

export type ResponsiveAppProps = {
  params: { type: string; projectId?: string; publicationId?: string };
  headers?: [string, string][];
  publication?: MediaProjectInstanceResponse | ApiError;
  publicationStatus?: number;
  responseHeaders?: [string, string][];
  isPreview?: boolean;
  hadInvalidPassword?: boolean;
  /** True when stored credentials (password or PIN token) were sent with the request.
   *  NOTE: Only needed for the publication_mode fallback check in ResponsiveAppWrapper.
   *  May be dropped in a future refactor if the API 401 path is confirmed sufficient. */
  hasStoredAuth?: boolean;
  organizationId?: string;
  organizationSlug?: string;
  instanceSlug?: string;
  errorDetail?: string;
  sentryEventId?: string;
  isDocustream?: boolean;
  /** Creator mode toolbar rendered above the right panel */
  creatorToolbar?: React.ReactNode;
  /** Compact mobile creator toolbar for Screen B document header */
  creatorToolbarMobile?: React.ReactNode;
  /** Creator mode content rendered inside the document card (e.g. chat input) */
  creatorDocumentFooter?: React.ReactNode;
  /** Creator mode content rendered below the document card (e.g. upgrade button) */
  creatorFooter?: React.ReactNode;
  /** Callback when user taps share in viewer mode (mobile doc header) */
  onShareClick?: () => void;
  /**
   * Enables the new layout mode (chat in right panel, drag-to-resize, etc.).
   * Defaults to false (legacy layout with chat in left panel).
   */
  newMode?: boolean;
  /**
   * True when launched as a SCORM activity (`?scorm=1`). Derived on the server
   * from searchParams and passed down so the new-layout decision is identical
   * on server and client (a SCORM launch runs inside an LMS iframe but should
   * still get the new UI). Reading the flag from `window` at render time would
   * cause an SSR/client hydration mismatch.
   */
  isScorm?: boolean;
  /**
   * True when the page was opened via an LTI launch (embedded in an LMS iframe).
   * Same SSR-safe pattern as isScorm — comes from a ?lti=1 query param set by
   * the LTI middleware, not from window, to avoid hydration mismatches.
   */
  isLti?: boolean;
  /** Optional content to render in the left panel instead of the video player */
  videoOverride?: React.ReactNode;
  /** When true, renders the "Hey Genius In Doc" layout: full-width document with floating circular video */
  isDocHeyGenius?: boolean;
  /** When true, replaces the floating video with an audio-reactive sphere driven by the same media URL */
  isAudioOnly?: boolean;
  /**
   * When true, the underlying media is still being generated. The InDoc
   * layout uses this to swap the floating video for a generating indicator
   * so users do not mistake the empty bubble for a broken player.
   */
  isMediaGenerating?: boolean;
  /**
   * Optional node rendered next to the Libertify logo inside the top
   * header bar (newMode desktop, when no creatorToolbar is provided).
   * Media-player uses this to mount its CTA pills.
   */
  headerSlot?: React.ReactNode;
  /** Rendered in every layout; MobileScreenSwap shows it on the video screen only. */
  footerSlot?: React.ReactNode;
};
